import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

export default function ResetPassword() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  const isRecovery = searchParams.get("type") === "recovery";
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password?type=recovery`,
      });

      if (error) throw error;

      toast({
        title: "Email sendt",
        description: "Tjek din indbakke for instruktioner til at nulstille din adgangskode",
      });
      
      setEmail("");
    } catch (error: any) {
      console.error("Error requesting password reset:", error);
      toast({
        variant: "destructive",
        title: "Fejl",
        description: error.message || "Kunne ikke sende reset-email",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Fejl",
        description: "Adgangskoderne matcher ikke",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        variant: "destructive",
        title: "Fejl",
        description: "Adgangskoden skal være mindst 6 tegn",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      toast({
        title: "Adgangskode opdateret",
        description: "Din nye adgangskode er nu aktiv",
      });
      
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast({
        variant: "destructive",
        title: "Fejl",
        description: error.message || "Kunne ikke opdatere adgangskode",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-md">
        <h1 className="text-4xl font-bold mb-8 text-center">
          {isRecovery ? "Ny adgangskode" : "Glemt adgangskode"}
        </h1>

        {!isRecovery ? (
          <form onSubmit={handleRequestReset} className="space-y-6">
            <p className="text-muted-foreground text-center mb-6">
              Indtast din email, så sender vi dig et link til at nulstille din adgangskode
            </p>
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@email.dk"
                required
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send reset-link
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/auth")}
              className="w-full"
            >
              Tilbage til login
            </Button>
          </form>
        ) : (
          <form onSubmit={handleUpdatePassword} className="space-y-6">
            <p className="text-muted-foreground text-center mb-6">
              Indtast din nye adgangskode
            </p>
            
            <div>
              <Label htmlFor="password">Ny adgangskode</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindst 6 tegn"
                required
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword">Bekræft adgangskode</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Gentag adgangskode"
                required
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Opdater adgangskode
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
